import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
  Link,
} from '@mui/material';
import { calculateEquitySplit } from './equitySplitUtils';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(value ?? 0);

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString('en-GB') : '');
const parseNumberInput = (value) => {
  const sanitized = typeof value === 'string' ? value.replace(/,/g, '') : value;
  const parsed = parseFloat(sanitized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const EquitySplitCalculatorApp = () => {
  const [marketValueGBP, setMarketValueGBP] = useState(350000);
  const [mortgageBalanceGBP, setMortgageBalanceGBP] = useState(210000);
  const [conveyancingCostsGBP, setConveyancingCostsGBP] = useState(2500);
  const [otherCostsGBP, setOtherCostsGBP] = useState(1500);
  const [splitCount, setSplitCount] = useState(2);
  const [splitPercent, setSplitPercent] = useState(50);

  const summary = useMemo(
    () =>
      calculateEquitySplit({
        marketValueGBP,
        mortgageBalanceGBP,
        conveyancingCostsGBP,
        otherCostsGBP,
        splitCount,
      }),
    [marketValueGBP, mortgageBalanceGBP, conveyancingCostsGBP, otherCostsGBP, splitCount]
  );

  const negativeEquity = summary.netEquity < 0;
  const showCustomSplit = summary.splitCount === 2;
  const personAShare = summary.netEquity * (splitPercent / 100);
  const personBShare = summary.netEquity - personAShare;

  return (
    <>
      <Helmet>
        <title>Property Buyout Calculator | Talk About Finance</title>
        <meta
          name="description"
          content="Split home equity fairly after paying off the mortgage and sale costs."
        />
        <link rel="canonical" href="https://talkabout.finance/apps/property-buyout-calculator" />
      </Helmet>

      <Container sx={{ mt: 6, mb: 8, pb: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.shadows[1],
            background: (theme) =>
              theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #f7f9fc 0%, #ffffff 45%, #f7f9fc 100%)'
                : 'linear-gradient(135deg, #1d1f24 0%, #17181c 45%, #1d1f24 100%)',
          }}
        >
          <Stack spacing={1.5}>
            <Box>
              <Button variant="outlined" color="primary" component={RouterLink} to="/apps">
                Back to apps
              </Button>
            </Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
              Calculator
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Property Buyout Calculator (UK)
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
              Estimate Equity & Buyout Costs
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
              Work out how much each person receives after paying off the mortgage and sale costs.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Want the step-by-step guide?{' '}
              <Link
                component={RouterLink}
                to="/articles/buying-someone-out-of-a-property-after-a-break-up-uk-guide"
                underline="none"
                color="primary"
                sx={{
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(25, 118, 210, 0.12)'
                      : 'rgba(144, 202, 249, 0.16)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(25, 118, 210, 0.18)'
                        : 'rgba(144, 202, 249, 0.24)',
                  },
                }}
              >
                Read the full buyout guide →
              </Link>
            </Typography>
          </Stack>
        </Paper>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Inputs
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Current market value"
                value={formatNumber(marketValueGBP)}
                onChange={(e) => setMarketValueGBP(parseNumberInput(e.target.value))}
                type="text"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  inputMode: 'decimal',
                }}
                helperText="Get at least two valuations (estate agents should provide them for free)."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Mortgage left"
                value={formatNumber(mortgageBalanceGBP)}
                onChange={(e) => setMortgageBalanceGBP(parseNumberInput(e.target.value))}
                type="text"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  inputMode: 'decimal',
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Split between"
                value={splitCount}
                onChange={(e) => setSplitCount(e.target.value)}
                type="number"
                fullWidth
                InputProps={{ inputProps: { min: 2, step: 1 } }}
                helperText="Number of people sharing the equity."
              />
            </Grid>
            {showCustomSplit && (
              <Grid item xs={12}>
                <Box sx={{ px: { xs: 1, md: 2 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Split percentage between two people
                  </Typography>
                  <Slider
                    value={splitPercent}
                    onChange={(_, value) => setSplitPercent(value)}
                    valueLabelDisplay="auto"
                    min={0}
                    max={100}
                  />
                  <Stack direction="row" spacing={2} justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Person A: {splitPercent}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Person B: {100 - splitPercent}%
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Costs
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Conveyancing costs"
                value={formatNumber(conveyancingCostsGBP)}
                onChange={(e) => setConveyancingCostsGBP(parseNumberInput(e.target.value))}
                type="text"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  inputMode: 'decimal',
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Other costs"
                value={formatNumber(otherCostsGBP)}
                onChange={(e) => setOtherCostsGBP(parseNumberInput(e.target.value))}
                type="text"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  inputMode: 'decimal',
                }}
                helperText="Include estate agent fees, repairs, or legal extras."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Summary
          </Typography>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Box>
                    <Typography color="text.secondary">Market value</Typography>
                    <Typography variant="h6">{formatCurrency(summary.marketValue)}</Typography>
                  </Box>
                  <Box>
                    <Typography color="text.secondary">Mortgage left</Typography>
                    <Typography variant="h6">{formatCurrency(summary.mortgageBalance)}</Typography>
                  </Box>
                  <Box>
                    <Typography color="text.secondary">Total costs</Typography>
                    <Typography variant="h6">{formatCurrency(summary.totalCosts)}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Box>
                    <Typography color="text.secondary">Net equity</Typography>
                    <Typography
                      variant="h6"
                      color={negativeEquity ? 'error.main' : 'text.primary'}
                    >
                      {formatCurrency(summary.netEquity)}
                    </Typography>
                  </Box>
                  {showCustomSplit ? (
                    <>
                      <Box>
                        <Typography color="text.secondary">Person A share</Typography>
                        <Typography
                          variant="h5"
                          color={negativeEquity ? 'error.main' : 'primary.main'}
                        >
                          {formatCurrency(personAShare)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="text.secondary">Person B share</Typography>
                        <Typography
                          variant="h5"
                          color={negativeEquity ? 'error.main' : 'primary.main'}
                        >
                          {formatCurrency(personBShare)}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box>
                      <Typography color="text.secondary">
                        Buyout amount per person (raise this to buy out the other owner, e.g. via remortgage)
                      </Typography>
                      <Typography
                        variant="h5"
                        color={negativeEquity ? 'error.main' : 'primary.main'}
                      >
                        {formatCurrency(summary.perPerson)}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          {showCustomSplit && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Use the split slider to match your agreement. The buyout amount is the other person’s share.
            </Typography>
          )}
          {negativeEquity && (
            <Typography sx={{ mt: 2, color: 'error.main' }}>
              The costs and mortgage exceed the market value, so there is no equity to split.
            </Typography>
          )}
          {!negativeEquity && summary.splitCount > 2 && (
            <Paper variant="outlined" sx={{ borderRadius: 2, mt: 3, p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Equity per person
              </Typography>
              <Grid container spacing={2}>
                {Array.from({ length: summary.splitCount }, (_, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`split-${index + 1}`}>
                    <Box>
                      <Typography color="text.secondary">Person {index + 1}</Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(summary.perPerson)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </Paper>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This calculator assumes all costs are paid from the sale proceeds before the remaining equity is split. It does not account for taxes or
            special agreements between owners.
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default EquitySplitCalculatorApp;
